import React, { useEffect, useMemo, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField, Typography } from "@material-ui/core";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import { Wrapper } from "components/wrapper";
import CustomBackdrop from "components/backdrop";
import MunicipalPortalAAppService from "application/platform/municipal-portal-service";
import {
  ResponsibleCard,
  ResponsibleGrid,
  ResponsibleName,
  ResponsiblePhoto,
  ResponsiblePhotoPlaceholder,
  ResponsibleRole,
} from "pages/organizationalStructure/organizationalStructure.styles";
import { SITUACAO_VEREADOR, formatDate, labelOf, mapToOptions } from "./camara-labels";

interface Vereador {
  id: number;
  nomVereador: string;
  nomPopular?: string;
  nomPartido?: string;
  textoBiografia?: string;
  fotoUrl?: string;
  indSituacao: number;
  dtaNascimento?: string;
  dtaInicioMandato?: string;
  dtaFimMandato?: string;
}

const getInitials = (name: string) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

export function VereadoresPage() {
  const [vereadores, setVereadores] = useState<Vereador[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [situacao, setSituacao] = useState("");
  const [selected, setSelected] = useState<Vereador | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    (async () => {
      try {
        setVereadores(await MunicipalPortalAAppService.listVereadores());
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = busca.trim().toLowerCase();
    return vereadores.filter(
      (v) =>
        (!situacao || String(v.indSituacao) === situacao) &&
        (!term || [v.nomVereador, v.nomPopular, v.nomPartido].some((field) => (field || "").toLowerCase().includes(term)))
    );
  }, [vereadores, busca, situacao]);

  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Vereadores" }]} />
      <PageTitle>VEREADORES</PageTitle>
      <Wrapper>
        <Grid container spacing={3} style={{ marginBottom: 24 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              variant="outlined"
              fullWidth
              label="Nome ou partido"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              variant="outlined"
              fullWidth
              label="Situação"
              value={situacao}
              onChange={(e) => setSituacao(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Todas</MenuItem>
              {mapToOptions(SITUACAO_VEREADOR).map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <CustomBackdrop open={loading} />
        {!loading && filtered.length === 0 && <Typography variant="body1">Nenhum vereador encontrado.</Typography>}

        <ResponsibleGrid>
          {filtered.map((v) => (
            <ResponsibleCard
              key={v.id}
              as="button"
              type="button"
              onClick={() => setSelected(v)}
              style={{ cursor: "pointer", font: "inherit" }}
            >
              {v.fotoUrl ? (
                <ResponsiblePhoto src={v.fotoUrl} alt={v.nomVereador} />
              ) : (
                <ResponsiblePhotoPlaceholder>{getInitials(v.nomVereador)}</ResponsiblePhotoPlaceholder>
              )}
              <ResponsibleName>{v.nomPopular || v.nomVereador}</ResponsibleName>
              <ResponsibleRole>
                {[v.nomPartido, labelOf(SITUACAO_VEREADOR, v.indSituacao)].filter(Boolean).join(" - ")}
              </ResponsibleRole>
            </ResponsibleCard>
          ))}
        </ResponsibleGrid>
      </Wrapper>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle>{selected.nomVereador}</DialogTitle>
            <DialogContent dividers>
              {selected.fotoUrl && (
                <img src={selected.fotoUrl} alt={selected.nomVereador} style={{ width: 120, height: 120, borderRadius: "50%", objectFit: "cover", marginBottom: 16 }} />
              )}
              {selected.nomPopular && (
                <Typography variant="body2">
                  <strong>Nome popular:</strong> {selected.nomPopular}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Partido:</strong> {selected.nomPartido || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Situação:</strong> {labelOf(SITUACAO_VEREADOR, selected.indSituacao)}
              </Typography>
              <Typography variant="body2">
                <strong>Nascimento:</strong> {formatDate(selected.dtaNascimento)}
              </Typography>
              <Typography variant="body2">
                <strong>Mandato:</strong> {formatDate(selected.dtaInicioMandato)} a {formatDate(selected.dtaFimMandato)}
              </Typography>
              {selected.textoBiografia && (
                <Typography variant="body2" style={{ marginTop: 16, whiteSpace: "pre-line" }}>
                  {selected.textoBiografia}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)} color="primary">
                Fechar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
